<template>
  <div class="ibox">
    <div class="ibox-title border-bottom-none m-b-none">
      <h1>
        <span v-if="profileName !== ''">{{ profileName }}'s</span>
        USA Wrestling Membership
        <span v-if="!requiresValidCard">(optional)</span>
      </h1>
      <p class="m-t-xs font-small">
        {{ promptText }}
      </p>
    </div>
    <div class="ibox-content">
      <spinner v-if="loading"></spinner>
      <div v-else>
        <!-- Most recent membership on file. A returning family has one record per season with the
             same number, so only the latest is shown; older seasons stay in the wrestler's history. -->
        <div v-if="visibleMemberships.length > 0" class="m-b-lg">
          <ul class="usaw-membership-list" role="list">
            <li
              class="usaw-membership-row"
              v-for="membership in visibleMemberships"
              :key="membership.id"
              :data-cy="`usaw-membership-${membership.id}`"
            >
              <div class="usaw-membership-main">
                <div class="font-semi">{{ membership.public_id }}</div>
                <div class="font-small text-gray-500">{{ membership.membership_type }}</div>
              </div>
              <div class="usaw-membership-status">
                <span :class="statusFor(membership).badgeClass">{{ statusFor(membership).label }}</span>
                <div class="font-small text-gray-500">{{ statusFor(membership).dateText }}</div>
              </div>
              <button
                v-if="redirect"
                type="button"
                class="usaw-membership-delete"
                :aria-label="`Delete USA Wrestling membership ${membership.public_id}`"
                @click="confirmDelete(membership)"
              >
                <i class="fa-regular fa-trash-can" aria-hidden="true"></i>
              </button>
            </li>
          </ul>
          <p class="alert alert-warning m-t-md m-b-none" v-if="!hasValidCard && hasUnverifiedCard">
            The USA Wrestling info on file for {{ profileName }} was entered by hand and has not been
            verified with USA Wrestling. Please follow the steps below so we can verify it.
          </p>
          <p class="alert alert-warning m-t-md m-b-none" v-else-if="!hasValidCard && requiresValidCard">
            {{ profileName }}'s USA Wrestling membership has expired. Please renew it, then enter the
            membership number below.
          </p>
          <p class="usaw-all-set m-b-none" v-else-if="hasValidCard" data-cy="usaw-all-set">
            <i class="fa-solid fa-circle-check color-green-600" aria-hidden="true"></i>
            <span v-if="!redirect">{{ profileName }} is all set for this season. Tap Next to continue.</span>
            <span v-else>{{ profileName }} is all set for this season.</span>
          </p>
        </div>

        <!-- The two steps: get it, then enter it. Rendered once. With a verified card on file
             the same list folds behind a summary, so the confirm panel is always available. -->
        <details :class="{ 'usaw-details usaw-details-first': hasValidCard }" :open="!hasValidCard" data-cy="usaw-steps">
          <summary v-show="hasValidCard">Enter a different membership number</summary>
        <ol class="usaw-steps" :class="{ 'm-t-md': hasValidCard }">
          <li class="usaw-step">
            <div class="usaw-step-num">1</div>
            <div class="usaw-step-body">
              <h3 class="usaw-step-title">Buy or renew {{ profileName }}'s membership</h3>
              <p class="font-small text-gray-500 m-t-xs">{{ purchaseHelp }}</p>
              <a target="_blank" rel="noopener" :href="purchaseUrl" class="btn usaw-btn" data-cy="usaw-purchase">
                Go to usawmembership.com <i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
              </a>
            </div>
          </li>
          <li class="usaw-step">
            <div class="usaw-step-num">2</div>
            <div class="usaw-step-body">
              <h3 class="usaw-step-title">Enter the membership number</h3>

              <!-- Confirm what USAW returned -->
              <div v-if="verification" class="m-t-sm" data-cy="usaw-confirm">
                <p class="font-semi m-b-xs" v-if="verification.active">Is this {{ profileName }}?</p>
                <p class="font-semi m-b-xs" v-else>This membership is not active</p>
                <!-- Name, type, and club only. USAW's photo and birthdate stay server side so a mistyped
                     number does not show a stranger's child. The dob check below still tells them if it is off. -->
                <div class="usaw-confirm-card">
                  <div class="usaw-confirm-body">
                    <h3 class="font-heavy m-y-none">{{ verification.name }}</h3>
                    <p class="m-b-none" v-if="verification.membership_type">{{ verification.membership_type }}</p>
                    <p class="m-b-none font-small text-gray-500" v-if="verification.primary_org">
                      Primary club: {{ verification.primary_org }}
                    </p>
                    <p class="m-b-none m-t-xs">
                      <span class="badge badge-primary" v-if="verification.active">Active</span>
                      <span class="badge badge-danger" v-else>Lapsed</span>
                    </p>
                    <p class="m-b-none font-small text-gray-500" v-if="verification.active">
                      Valid through {{ readableDate(verification.expires_on) }}
                    </p>
                  </div>
                </div>
                <template v-if="verification.active">
                  <p class="alert alert-warning m-t-md" v-if="verification.dob_matches === false" data-cy="usaw-dob-warning">
                    <strong>Heads up:</strong> USA Wrestling has a different birthdate on file for this member than we have
                    for {{ profileName }}. Make sure this is {{ profileName }}'s card and not a sibling's or a parent's.
                    If USA Wrestling is right, you can fix {{ profileName }}'s birthdate on the previous screen.
                  </p>
                  <p class="alert alert-warning m-t-md" v-if="verification.already_used_by" data-cy="usaw-reuse-warning">
                    <strong>Heads up:</strong> This membership number is already on file for {{ verification.already_used_by }}.
                    Each wrestler needs their own USA Wrestling membership.
                  </p>
                  <div class="usaw-confirm-actions m-t-md">
                    <button type="button" class="btn btn-default" @click="resetVerification" data-cy="usaw-retry">
                      No, try another number
                    </button>
                    <button type="button" class="btn btn-primary" @click="attachMembership" :disabled="saving" data-cy="usaw-submit">
                      Yes, add this membership
                    </button>
                  </div>
                </template>
                <template v-else>
                  <p class="alert alert-warning m-t-md">
                    USA Wrestling reports that this membership is not active for the current season.
                    <a :href="purchaseUrl" target="_blank" rel="noopener" class="text-underline">Renew it on usawmembership.com</a>,
                    then come back and enter the number again. Memberships run September 1 through August 31.
                  </p>
                  <div class="m-t-md flex">
                    <button type="button" class="btn btn-default" @click="resetVerification" data-cy="usaw-retry">
                      Try another number
                    </button>
                  </div>
                </template>
              </div>

              <!-- Enter a number -->
              <div v-else>
                <p class="font-small text-gray-500 m-t-xs">It's on the card, in USA Wrestling's confirmation email, and on the wrestler's profile at usawmembership.com.</p>
                <div class="form-group m-b-none">
                  <label for="usaw_id">Membership number</label>
                  <div class="usaw-number-row">
                    <input
                      id="usaw_id"
                      name="usaw_id"
                      data-cy="usaw-id"
                      v-model="usawId"
                      type="text"
                      inputmode="numeric"
                      autocomplete="off"
                      class="form-control font-no-ios-zoom"
                      placeholder="e.g. 2316781701"
                      @keyup.enter="verify"
                    >
                    <button type="button" :class="verifyButtonClass" @click="verify" :disabled="verifying" data-cy="usaw-verify">
                      <i class="fa-solid fa-magnifying-glass" v-if="!verifying"></i>
                      <i class="fa-solid fa-spinner fa-spin" v-else></i>
                      Verify
                    </button>
                  </div>
                  <div class="text-gray-500 text-12 m-t-xs">
                    We check the number with USA Wrestling, so there's no card photo to upload.
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ol>
        </details>

        <p class="text-danger m-t-m" v-if="error" data-cy="usaw-error">
          {{ errorMessage }}
          <a href="#" @click.prevent="verify" v-if="unavailable" class="m-l-xs text-underline">Retry</a>
        </p>

        <details class="usaw-details" :class="{ 'usaw-details-first': !hasValidCard }">
          <summary>{{ orgCta }}</summary>
          <p class="m-t-sm">
            USA Wrestling is the national governing body for wrestling in the United States. It charters clubs
            like {{ teamName }} and sanctions over 2,100 competitions a year for its members. A membership gives {{ profileName }}:
          </p>
          <ul class="list-unstyled org-features">
            <li class="m-b-xs"><i class="fa-solid fa-check"></i> Secondary sports accident coverage for injuries sustained while wrestling</li>
            <li class="m-b-xs"><i class="fa-solid fa-check"></i> Entry to local, regional, and national competitions</li>
            <li v-if="requiresValidCard" class="m-b-xs">
              <i class="fa-solid fa-check"></i> Compliance with {{ teamName }}'s USA Wrestling Club Charter, which requires every athlete to hold an active membership to practice
            </li>
          </ul>
          <p class="font-small text-gray-500 m-b-none">
            Questions about buying a membership? See
            <a :href="helpUrl" target="_blank" rel="noopener" class="text-underline">USA Wrestling's help page</a>.
          </p>
        </details>

        <div class="m-t-lg flex flex-col align-items-end" v-if="!redirect">
          <button
            type="button"
            data-cy="submit"
            :class="nextButtonClass"
            :disabled="nextDisabled"
            @click="$emit('membership-completed')"
          >
            Next
          </button>
          <p class="font-small text-gray-500 m-t-xs m-b-none" v-if="nextDisabled">
            Verify {{ profileName }}'s membership number to continue.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import swal from 'sweetalert2';
import { errorableMix } from './mixins/errorable_mix';
import Spinner from './spinner.vue';

/*
 * USA Wrestling membership step, backed by USAW's API.
 *
 * The registrant types a membership number, we look it up (POST usaw_verifications),
 * show them who USAW says that is, and only on confirmation attach it
 * (POST usaw_memberships, which re-verifies server side). Nothing here decides
 * validity: the server does. This replaces membership-manager +
 * insurance-membership-form for USAW; AAU still uses those.
 */
export default {
  name: 'usaw-membership-verifier',
  components: {
    Spinner,
  },
  mixins: [errorableMix],
  props: {
    wrestlerId: {
      type: Number,
      required: true,
    },
    mostRecentId: {
      type: String,
      default: '',
    },
    config: {
      type: String,
      default: 'usaw_disabled',
    },
    teamName: {
      type: String,
      default: '',
    },
    profileName: {
      type: String,
      default: '',
    },
    // True on the standalone /wrestlers/:id/usaw/edit page: no Next button, delete is available,
    // and we go back to the wrestler after saving. In the registration flow delete is hidden: a new
    // verified card supersedes the old record and the flow should only move forward.
    redirect: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      verifying: false,
      saving: false,
      unavailable: false,
      memberships: [],
      usawId: '',
      verification: null,
      purchaseUrl: 'https://www.usawmembership.com',
      helpUrl: 'https://www.usawmembership.com/help/category/3/question/14',
    };
  },
  computed: {
    requiresValidCard() {
      return this.config === 'usaw_require_all' || this.config === 'usaw_require_id_and_expires';
    },
    // Only a card verified with USAW that is still inside its membership year counts.
    // Legacy hand-entered cards do not, whatever expiration they claim.
    hasValidCard() {
      return _.some(this.memberships, (m) => m.verified_at && moment().isSameOrBefore(m.expires_on, 'day'));
    },
    hasUnverifiedCard() {
      return _.some(this.memberships, (m) => !m.verified_at);
    },
    // Newest record first: latest expiration, then most recently created. Only that one is shown.
    visibleMemberships() {
      const sorted = _.orderBy(this.memberships, [(m) => moment(m.expires_on).valueOf(), 'id'], ['desc', 'desc']);
      return sorted.slice(0, 1);
    },
    nextDisabled() {
      return !this.hasValidCard && this.requiresValidCard;
    },
    // One solid primary at a time: Verify while a card is still needed, Next once it is verified.
    verifyButtonClass() {
      return this.hasValidCard ? 'btn btn-default' : 'btn btn-primary';
    },
    nextButtonClass() {
      return this.nextDisabled ? 'btn btn-default' : 'btn btn-primary';
    },
    promptText() {
      if (this.hasValidCard) {
        return this.requiresValidCard
          ? `${this.teamName} requires an active USA Wrestling membership to practice. ${this.profileName} is covered for this season.`
          : `${this.profileName}'s USA Wrestling membership is verified for this season.`;
      }
      if (this.requiresValidCard) {
        return `${this.teamName} requires an active USA Wrestling membership to practice. Two quick steps.`;
      }
      return `A USA Wrestling membership is optional for ${this.teamName}. If ${this.profileName} has one, or you would like one, here is how.`;
    },
    purchaseHelp() {
      const club = this.teamName ? ` You will be asked which club ${this.profileName} trains with: choose ${this.teamName}.` : '';
      if (this.requiresValidCard) {
        return `Buy it on USA Wrestling's site. It takes about 5 minutes.${club} Already have one for this season? Skip to step 2.`;
      }
      return `Buy it on USA Wrestling's site. It takes about 5 minutes.${club} Already have one? Skip to step 2.`;
    },
    orgCta() {
      if (this.requiresValidCard) {
        return `Why does ${this.profileName} need this?`;
      }
      return `Why would ${this.profileName} want this?`;
    },
  },
  created() {
    this.usawId = this.mostRecentId;
    this.getMemberships();
  },
  methods: {
    readableDate(date) {
      return moment(date).format('MMM Do YYYY');
    },
    statusFor(membership) {
      const current = moment().isSameOrBefore(membership.expires_on, 'day');
      if (membership.revoked_at) {
        return {
          label: 'No longer active',
          badgeClass: 'badge badge-danger',
          dateText: `USA Wrestling reported this membership inactive on ${this.readableDate(membership.revoked_at)}`,
        };
      }
      if (!membership.verified_at) {
        return {
          label: 'Unverified',
          badgeClass: 'badge',
          dateText: `Entered by hand, ${current ? 'expires' : 'expired'} ${this.readableDate(membership.expires_on)}`,
        };
      }
      if (current) {
        return { label: 'Verified', badgeClass: 'badge badge-primary', dateText: `Valid through ${this.readableDate(membership.expires_on)}` };
      }
      return { label: 'Expired', badgeClass: 'badge badge-danger', dateText: `Expired ${this.readableDate(membership.expires_on)}` };
    },
    getMemberships() {
      const vm = this;
      vm.loading = true;
      axios
        .get(vm.$apiService.usawMemberships(vm.wrestlerId))
        .then((response) => {
          vm.loading = false;
          vm.memberships = response.data.usaw_memberships;
        })
        .catch((error) => {
          vm.loading = false;
          vm.showError(`Error retrieving memberships ${error.toString()}`);
        });
    },
    verify() {
      const vm = this;
      if (vm.verifying) {
        return;
      }
      vm.clearError();
      vm.unavailable = false;
      if (vm.usawId.replace(/\D/g, '').length === 0) {
        vm.showError('Please enter a USA Wrestling membership number.');
        return;
      }

      vm.verifying = true;
      axios
        .post(vm.$apiService.usawVerifications(vm.wrestlerId), { usaw_id: vm.usawId })
        .then((response) => {
          vm.verifying = false;
          vm.verification = response.data;
        })
        .catch((error) => {
          vm.verifying = false;
          vm.handleUsawError(error);
        });
    },
    attachMembership() {
      const vm = this;
      if (vm.saving || !vm.verification) {
        return;
      }
      vm.clearError();
      vm.saving = true;
      axios
        .post(vm.$apiService.usawMemberships(vm.wrestlerId), {
          usaw_membership: { public_id: vm.verification.usaw_id },
        })
        .then((response) => {
          vm.saving = false;
          vm.memberships.unshift(response.data);
          vm.verification = null;
          vm.usawId = '';
          vm.$notificationManager.emit('show-toast', 'USA Wrestling membership verified', true);
          if (vm.redirect) {
            window.location.assign(`/wrestlers/${vm.wrestlerId}`);
          }
        })
        .catch((error) => {
          vm.saving = false;
          vm.handleUsawError(error);
        });
    },
    handleUsawError(error) {
      const status = error.response?.status;
      this.unavailable = status === 503;
      const errors = error.response?.data?.errors;
      if ((status === 404 || status === 422 || status === 503) && Array.isArray(errors) && errors.length > 0) {
        // The server's messages are full sentences already, so no readableMessageFrom (it adds a period)
        this.showError(errors.join(' '));
      } else if (status === 429) {
        this.showError('Too many lookups in a short time. Please wait a few minutes and try again.');
      } else {
        this.showError('Error verifying with USA Wrestling, please try again later or contact support@wrestlingiq.com');
      }
    },
    resetVerification() {
      this.verification = null;
      this.clearError();
    },
    confirmDelete(membership) {
      const vm = this;
      swal
        .fire({
          icon: 'warning',
          title: `Are you sure you want to delete USA Wrestling #${membership.public_id}?`,
          text: `This will remove the USA Wrestling membership info from ${this.profileName} and regenerate their WIQ ID card.`,
          showCancelButton: true,
          reverseButtons: true,
          confirmButtonText: 'Yes, delete the membership',
          cancelButtonText: 'No, keep it',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger m-l-md',
            cancelButton: 'btn btn-default',
          },
        })
        .then((result) => {
          if (result.value) {
            vm.destroyMembership(membership);
          }
        });
    },
    destroyMembership(membership) {
      const vm = this;
      if (vm.loading) {
        return;
      }
      vm.loading = true;
      vm.clearError();
      axios
        .delete(vm.$apiService.usawMembership(vm.wrestlerId, membership.id))
        .then(() => {
          vm.loading = false;
          vm.$notificationManager.emit('show-toast', 'Membership deleted', true);
          vm.memberships = _.reject(vm.memberships, ['id', membership.id]);
        })
        .catch(() => {
          vm.loading = false;
          vm.showError('Error deleting membership, please try again later or contact support@wrestlingiq.com');
        });
    },
  },
};
</script>
